import {
  ExportType,
  ExportFormat,
  TableExportPdf,
  TableExportXlsx,
  TablesExportPdf,
  TablesExportXlsx,
} from '../../../exports';
import { ColumnHandler } from './ColumnHandler';
import { RowHandler } from './RowHandler';

type ExportMapper = {
  [K in ExportFormat]: (
    name: string,
    footerText?: string,
    pageTemplate?: string,
    includeTotals?: boolean
  ) => ExportType<K>;
};

type ExportBehavior = {
  includeTotals?: boolean;
};

type ExportCustomGenerator<K extends ExportFormat> = ExportBehavior & {
  generator: (table: ExportType<K>, fileName: string) => void;
  isDispatchAction: boolean;
};

export type ExportConfig = Partial<{
  [K in ExportFormat]: boolean | ExportBehavior | ExportCustomGenerator<K>;
}>;

const exportsDefault: {
  [K in ExportFormat]: (
    fileName: string,
    footerText?: string,
    pageTemplate?: string
  ) => (table: ExportType<K>) => void;
} = {
  PDF: (fileName, footerText, pageTemplate) => table => {
    const tablesExport = new TablesExportPdf();
    tablesExport.addTable('table', 1, table);
    tablesExport.export(fileName, footerText, pageTemplate);
  },
  XLSX: fileName => table => {
    const tablesExport = new TablesExportXlsx();
    tablesExport.addTable('table', 1, table);
    tablesExport.export(fileName);
  },
};

export class ExportHandler<TData, TContext> {
  #columnHandler: ColumnHandler<TData, TContext>;

  #rowHandler: RowHandler<TData, TContext>;

  #exportConfig: ExportConfig;

  #exportMapper: ExportMapper;

  constructor(
    columnHandler: ColumnHandler<TData, TContext>,
    rowHandler: RowHandler<TData, TContext>,
    exportConfig?: ExportConfig
  ) {
    this.#columnHandler = columnHandler;
    this.#rowHandler = rowHandler;
    this.#exportConfig = exportConfig || {};
    this.#exportMapper = {
      XLSX: (name: string, _footerText?: string, _pageTemplate?: string, includeTotals = true) =>
        this.tableToXlsx(name, includeTotals),
      PDF: (name: string, footerText?: string, _pageTemplate?: string, includeTotals = true) =>
        this.tableToPdf(name, footerText, includeTotals),
    };
  }

  private tableToXlsx(sheetName: string, includeTotals: boolean): TableExportXlsx {
    const columns = this.#columnHandler.getVisibleColumns();
    const currentTotals = this.#rowHandler.totals;
    const headers = columns.map(i => i.getTitle());
    const rows: TableExportXlsx['rows'] = this.#rowHandler
      .getRows(true)
      .map(row => row.cells.map(cell => cell.getValue()));

    const totals = columns.map(i => {
      const total = currentTotals.find(c => c.columnId === i.id);
      if (total) return total.value;
      return null;
    });

    return { headers, rows, sheetName, ...(includeTotals ? { totals } : {}) };
  }

  private tableToPdf(title: string, footerText?: string, includeTotals = true): TableExportPdf {
    const columns = this.#columnHandler.getVisibleColumns();
    const currentTotals = this.#rowHandler.totals;
    const headers = columns.map(i => i.getTitle());
    const columnConfig = columns.map(i => ({ width: i.getWidth().pt, align: i.align }));
    const totals = columns.map(i => {
      const total = currentTotals.find(c => c.columnId === i.id);
      if (total) return i.getValueFormatted(total.value);
      return '';
    });
    const rows: TableExportPdf['rows'] = this.#rowHandler
      .getRows(true)
      .map(row => row.cells.map(cell => cell.getValueFormatted()));

    return {
      headers,
      rows,
      title,
      columnConfig,
      totals: includeTotals && totals.some(i => i) ? totals : [],
      footerText,
    };
  }

  private static hasCustomGenerator<K extends ExportFormat>(
    value: ExportConfig[K]
  ): value is ExportCustomGenerator<K> {
    return Boolean(value) && typeof value === 'object' && 'generator' in value;
  }

  private resolveIncludeTotals(format: ExportFormat): boolean {
    const exportValue = this.#exportConfig[format];

    if (exportValue && typeof exportValue === 'object' && 'includeTotals' in exportValue) {
      return exportValue.includeTotals !== false;
    }

    return true;
  }

  setConfig(config: ExportConfig) {
    this.#exportConfig = config;
  }

  export(
    format: ExportFormat,
    title: string,
    fileName: string,
    footerText?: string,
    pageTemplate?: string
  ) {
    const exportValue = this.#exportConfig[format];
    if (exportValue === false) throw new Error(`La exportación a ${format} no está habilitada`);
    const includeTotals = this.resolveIncludeTotals(format);

    const table = this.#exportMapper[format](
      title,
      footerText,
      pageTemplate,
      includeTotals
    ) as never;

    if (
      exportValue === undefined ||
      exportValue === true ||
      !ExportHandler.hasCustomGenerator(exportValue)
    ) {
      exportsDefault[format](fileName, footerText, pageTemplate)(table);
    } else {
      exportValue.generator(table, title);
    }
  }

  hasDispatchAction(format: ExportFormat) {
    const exportValue = this.#exportConfig[format];
    if (!ExportHandler.hasCustomGenerator(exportValue)) return false;
    return exportValue.isDispatchAction;
  }
}
